/**
 * Schémas Zod — moteur de formulaires versionnés (F2-Forms-1a / 9.U.1).
 *
 * CONTRAT + HELPERS uniquement (aucune route API — cf. chunk 1b). Couvre :
 *   - enums (status / scope / submit_source / field types) ;
 *   - le contrat `form_template_versions.schema` v1 (sections → champs, bilingue
 *     strict {fr,en}, clés stables, garde-fous options/limites) ;
 *   - les row-schemas template / version / response ;
 *   - `buildFormResponseDataSchema()` : construit un validateur Zod des réponses
 *     À PARTIR d'une version publiée précise (rejette clés inconnues, vérifie
 *     requis/types/options) — jamais contre « la dernière version ».
 *
 * Architecture prête (sans coder Forms-3) : clés de champ STABLES (agrégation
 * cross-version), `data` distinct du template, snapshot de version porté par la
 * réponse (`template_version_id` immuable DB), provenance/préremplissage futurs
 * s'ajouteront dans `data` sans refonte. `schema_version` figé à 1 (ADR Q15).
 */
import { z } from "zod";

import { PaginationSchema, UuidSchema } from "./common.js";
import { DocumentProfileSchema } from "./document-profile.js";

// ---------------------------------------------------------------------------
// Enums (miroir des enums DB de la migration 0055)
// ---------------------------------------------------------------------------
export const FORM_TEMPLATE_STATUSES = ["active", "archived"] as const;
export const FORM_TEMPLATE_SCOPES = ["tenant_default", "company", "product", "project"] as const;
export const FORM_RESPONSE_SUBMIT_SOURCES = [
  "public_magic_link",
  "broker_ui",
  "api",
  "mcp",
  "preview",
] as const;
export const FORM_FIELD_TYPES = [
  "text",
  "textarea",
  "email",
  "number",
  "date",
  "select",
  "multi_select",
  "checkbox",
  "file",
  "company_ref",
  "contact_ref",
] as const;

export const FormTemplateStatusSchema = z.enum(FORM_TEMPLATE_STATUSES);
export const FormTemplateScopeSchema = z.enum(FORM_TEMPLATE_SCOPES);
export const FormResponseSubmitSourceSchema = z.enum(FORM_RESPONSE_SUBMIT_SOURCES);
export const FormFieldTypeSchema = z.enum(FORM_FIELD_TYPES);

export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

// ---------------------------------------------------------------------------
// Rôle de champ (F2-Forms-1e-B) — attribut OPTIONNEL qui déclare comment la
// valeur d'un champ pré-remplit la matérialisation d'un intake_event
// (`form_response.data` → wizard materialize, côté front, revue humaine).
// Jamais consommé par `FormRenderer` ni par `buildFormResponseDataSchema`.
// `insurer_codes` volontairement HORS jeu v1 (source de vérité non tranchée —
// dette D-NEW-FORMS-1E-INSURER-SOURCE). Enum extensible sans migration.
// ---------------------------------------------------------------------------
export const FORM_FIELD_ROLES = [
  "project_title",
  "company_legal_name",
  "employee_count",
  "effect_date",
  "renewal_target_date",
  "deadline_date",
  /**
   * 9.BJ.1 — les trois rôles d'un document qui se SIGNE.
   *
   * Le serveur doit savoir lequel des champs porte le nom du signataire, lequel
   * porte sa fonction, et lequel porte le consentement — sans jamais connaître
   * un nom de champ particulier ni un gabarit particulier. Un cabinet qui écrit
   * son propre mandat pose ces rôles sur ses champs et le mécanisme fonctionne
   * sans déploiement, ce qu'un `field_key` codé en dur n'aurait pas permis.
   *
   * `signature_consent` est une case : cochée, elle vaut consentement explicite.
   * Non cochée, la soumission est refusée — un mandat qu'on n'a pas accepté
   * n'est pas un mandat.
   */
  "signatory_name",
  "signatory_title",
  "signature_consent",
] as const;

export const FormFieldRoleSchema = z.enum(FORM_FIELD_ROLES);
export type FormFieldRole = (typeof FORM_FIELD_ROLES)[number];

/**
 * Compatibilité type↔rôle (source de vérité unique — consommée par le
 * `superRefine` publish, le pré-check éditeur/publication et le mapper prefill).
 * Un rôle ne peut être porté que par un champ d'un type compatible.
 */
export const FIELD_ROLE_ALLOWED_TYPES: Record<FormFieldRole, readonly FormFieldType[]> = {
  project_title: ["text"],
  company_legal_name: ["text"],
  employee_count: ["number"],
  effect_date: ["date"],
  renewal_target_date: ["date"],
  deadline_date: ["date"],
  signatory_name: ["text"],
  signatory_title: ["text"],
  signature_consent: ["checkbox"],
};

export function roleAllowsType(role: FormFieldRole, type: FormFieldType): boolean {
  return FIELD_ROLE_ALLOWED_TYPES[role].includes(type);
}

/** Issue de validation de rôles (mappée en microcopie localisée côté client). */
export type FieldRoleIssue =
  | { kind: "type_mismatch"; role: FormFieldRole; field_key: string; field_type: FormFieldType }
  | { kind: "duplicate"; role: FormFieldRole; field_keys: string[] };

/**
 * Collecte les violations de rôles d'une version (compat type↔rôle + unicité
 * d'un rôle par version). Pur, sans dépendance Zod — réutilisé au **publish**
 * (superRefine) ET par le pré-check client (microcopie utile, aucun nouveau
 * code bilingue). Ne signale rien pour les champs sans rôle.
 */
export function collectFieldRoleIssues(
  sections: ReadonlyArray<{ fields: ReadonlyArray<{ key: string; type: string; role?: string }> }>,
): FieldRoleIssue[] {
  const issues: FieldRoleIssue[] = [];
  const byRole = new Map<FormFieldRole, string[]>();
  for (const section of sections) {
    for (const f of section.fields) {
      if (!f.role) continue;
      const role = f.role as FormFieldRole;
      if (!(FORM_FIELD_ROLES as readonly string[]).includes(role)) continue;
      if (!roleAllowsType(role, f.type as FormFieldType)) {
        issues.push({
          kind: "type_mismatch",
          role,
          field_key: f.key,
          field_type: f.type as FormFieldType,
        });
      }
      byRole.set(role, [...(byRole.get(role) ?? []), f.key]);
    }
  }
  for (const [role, keys] of byRole) {
    if (keys.length > 1) issues.push({ kind: "duplicate", role, field_keys: keys });
  }
  return issues;
}

// ---------------------------------------------------------------------------
// Contrat `schema` JSONB v1 — garde-fous
// ---------------------------------------------------------------------------
// Clé stable : identifie une réponse indépendamment du libellé (ADR Q3 + §6 PO).
const StableKeySchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]{0,62}$/, "clé stable requise: ^[a-z][a-z0-9_]{0,62}$");

// Libellé bilingue STRICT — refuse l'unilingue (ADR Q3).
export const BilingualLabelSchema = z
  .object({ fr: z.string().min(1).max(300), en: z.string().min(1).max(300) })
  .strict();

// Regex utilisateur bornée (anti-ReDoS minimal : longueur plafonnée ; analyse
// catastrophique complète = dette Phase 2).
const FieldValidationSchema = z
  .object({
    min_length: z.number().int().min(0).max(10000).optional(),
    max_length: z.number().int().min(1).max(10000).optional(),
    regex: z.string().min(1).max(200).optional(),
    min: z.number().finite().optional(),
    exclusive_min: z.number().finite().optional(),
    max: z.number().finite().optional(),
    integer: z.boolean().optional(),
  })
  .strict()
  .refine(
    (v) => v.min_length === undefined || v.max_length === undefined || v.min_length <= v.max_length,
    { message: "min_length doit être <= max_length" },
  )
  .refine((v) => v.min === undefined || v.max === undefined || v.min <= v.max, {
    message: "min must be <= max",
  })
  .refine((v) => v.exclusive_min === undefined || v.max === undefined || v.exclusive_min < v.max, {
    message: "exclusive_min must be < max",
  });

const FieldOptionSchema = z
  .object({ value: StableKeySchema, label: BilingualLabelSchema })
  .strict();

/**
 * D'où le SERVEUR peut amorcer la valeur d'un champ — chunk 9.BJ.3.
 *
 * ## Pourquoi une propriété distincte de `role`
 *
 * `FORM_FIELD_ROLES` déclare comment la valeur d'un champ **pré-remplit la
 * matérialisation** d'un intake_event : direction *formulaire → système*. Ici
 * c'est l'inverse, *système → formulaire*. Faire porter les deux sens au même
 * attribut signifierait qu'ajouter une source d'amorçage **changerait aussi ce
 * que fait la matérialisation** sur des versions déjà publiées et immuables.
 *
 * Et la couverture n'y était pas : des quatre sources utiles, `role` n'en
 * connaissait qu'une (`company_legal_name`). `signatory_title` coïncide avec le
 * titre du contact par accident, pas par sémantique.
 *
 * ## Ce que ça ne fait PAS
 *
 * Amorcer n'est pas imposer. Le serveur SUGGÈRE une valeur à l'ouverture ; la
 * personne qui remplit peut la corriger, et c'est **sa** valeur qui est
 * enregistrée. Rien n'est réinjecté à la soumission — sans quoi on ferait signer
 * autre chose que ce qui était à l'écran.
 *
 * L'énumération s'étend sans migration : elle vit dans le `schema` jsonb et est
 * validée applicativement, comme les rôles.
 */
export const FORM_PREFILL_SOURCES = [
  /** `app.companies.legal_name` de l'entreprise du dossier. */
  "company.legal_name",
  /** `app.contacts.full_name` du contact À QUI le lien a été envoyé. */
  "contact.full_name",
  /** `app.contacts.title` du même contact. */
  "contact.title",
  /** `app.users.full_name` du conseiller assigné au dossier. */
  "broker.full_name",
] as const;
export const FormPrefillSourceSchema = z.enum(FORM_PREFILL_SOURCES);
export type FormPrefillSource = (typeof FORM_PREFILL_SOURCES)[number];

/**
 * Compatibilité type↔source — même patron que `FIELD_ROLE_ALLOWED_TYPES`.
 *
 * Les quatre sources sont des chaînes courtes : `text` ou `textarea`. Déclarer
 * une source sur une case à cocher ou une date produirait une valeur que le
 * champ ne sait pas porter, donc un amorçage silencieusement ignoré — pire
 * qu'un refus, parce que personne ne saurait pourquoi le champ reste vide.
 */
export const PREFILL_ALLOWED_TYPES: Record<FormPrefillSource, readonly FormFieldType[]> = {
  "company.legal_name": ["text", "textarea"],
  "contact.full_name": ["text", "textarea"],
  "contact.title": ["text", "textarea"],
  "broker.full_name": ["text", "textarea"],
};

export function prefillAllowsType(source: FormPrefillSource, type: FormFieldType): boolean {
  return PREFILL_ALLOWED_TYPES[source].includes(type);
}

export const FormFieldConditionsSchema = z
  .array(
    z
      .object({
        field_key: StableKeySchema,
        values: z.array(StableKeySchema).min(1).max(100),
      })
      .strict(),
  )
  .min(1)
  .max(10);
export type FormFieldConditions = z.infer<typeof FormFieldConditionsSchema>;

export const FormFieldSchema = z
  .object({
    key: StableKeySchema,
    type: FormFieldTypeSchema,
    label: BilingualLabelSchema,
    help: BilingualLabelSchema.optional(),
    placeholder: BilingualLabelSchema.optional(),
    required: z.boolean(),
    active_when: FormFieldConditionsSchema.optional(),
    required_when: FormFieldConditionsSchema.optional(),
    options: z.array(FieldOptionSchema).min(1).max(100).optional(),
    validation: FieldValidationSchema.optional(),
    pdf_format: z.enum(["decimal", "cad", "percent"]).optional(),
    // 1e-B — rôle de mapping optionnel (structurel : enum connu accepté au draft
    // ET au publish ; la compat type↔rôle + l'unicité sont vérifiées au PUBLISH
    // uniquement, cf. FormVersionSchemaContract.superRefine).
    role: FormFieldRoleSchema.optional(),
    // 9.BJ.3 — d'où le serveur amorce la valeur. Structurel au brouillon comme
    // à la publication ; la compat type↔source est vérifiée juste en dessous,
    // parce qu'elle ne dépend que du champ lui-même (contrairement aux rôles,
    // dont l'unicité se juge à l'échelle de la version).
    prefill_from: FormPrefillSourceSchema.optional(),
  })
  .strict()
  .superRefine((f, ctx) => {
    if (
      f.type !== "number" &&
      (f.validation?.min !== undefined ||
        f.validation?.exclusive_min !== undefined ||
        f.validation?.max !== undefined ||
        f.validation?.integer !== undefined)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["validation"],
        message: "numeric constraints require a number field",
      });
    }
    if (f.pdf_format && f.type !== "number")
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["pdf_format"],
        message: "PDF number format requires a number field",
      });
    if (f.prefill_from && !prefillAllowsType(f.prefill_from, f.type)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `source de préremplissage ${f.prefill_from} incompatible avec le type ${f.type}`,
        path: ["prefill_from"],
      });
    }
    const needsOptions = f.type === "select" || f.type === "multi_select";
    if (needsOptions && !f.options) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "options requis pour select/multi_select",
        path: ["options"],
      });
    }
    if (!needsOptions && f.options) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "options interdit hors select/multi_select",
        path: ["options"],
      });
    }
    if (f.options) {
      const values = f.options.map((o) => o.value);
      if (new Set(values).size !== values.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "clés d'options dupliquées",
          path: ["options"],
        });
      }
    }
  });

/**
 * Un bloc de CONTENU — chunk 9.BJ.2.
 *
 * ## Pourquoi un tableau à part, et jamais un type de champ
 *
 * `buildFormResponseDataSchema()` construit une clé de réponse pour **chaque**
 * champ de chaque section, et l'objet produit est `.strict()`. Un paragraphe
 * déclaré comme champ créerait donc une clé dans le contrat de réponse — un
 * client pourrait y poster une valeur, et elle serait acceptée. Il honorerait
 * aussi `required`, atterrirait dans `form_response.data`, et se verrait
 * proposer un `role` dans l'éditeur.
 *
 * Les blocs vivent donc à côté des champs. Le validateur de réponse n'itère que
 * `fields` : **qu'un bloc ne produise aucune clé n'est pas une promesse, c'est
 * une conséquence structurelle**, et un test le vérifie.
 *
 * ## Texte BRUT, jamais de balisage
 *
 * `text` est une chaîne, rendue en coupant sur les lignes vides (paragraphes) et
 * sur les retours simples (sauts de ligne). Aucun HTML n'est stocké ni rendu :
 * ces blocs s'affichent sur une surface publique non authentifiée, et une
 * chaîne inerte n'a aucune surface d'injection à défendre.
 *
 * ## Ordre
 *
 * Les blocs d'une section s'affichent AVANT ses champs. Pas de position
 * relative aux champs en v1 : un mandat pose son corps puis ses signatures, et
 * une clé de position serait une règle de plus à valider pour un besoin qui
 * n'existe pas encore.
 */
export const FORM_BLOCK_KINDS = ["paragraph"] as const;
export const FormBlockKindSchema = z.enum(FORM_BLOCK_KINDS);
export type FormBlockKind = (typeof FORM_BLOCK_KINDS)[number];

/** Texte bilingue d'un bloc. Plus long qu'un libellé — c'est un corps de document. */
const BlockTextSchema = z
  .object({
    fr: z.string().trim().min(1).max(20_000),
    en: z.string().trim().min(1).max(20_000),
  })
  .strict();

export const FormContentBlockSchema = z
  .object({
    key: StableKeySchema,
    kind: FormBlockKindSchema,
    text: BlockTextSchema,
  })
  .strict();
export type FormContentBlock = z.infer<typeof FormContentBlockSchema>;

// ---------------------------------------------------------------------------
// 9.BJ.7 — interpolation des blocs de contenu
// ---------------------------------------------------------------------------
/**
 * Un bloc peut nommer un champ de sa version : `{{cle_du_champ}}`.
 *
 * ## Pourquoi cette mécanique existe
 *
 * Le mandat d'étude réel est parti en production avec la chaîne
 * `[Nom du conseiller]` imprimée telle quelle **au milieu du paragraphe qui
 * engage le signataire**. Le texte statique et les réponses vivaient dans deux
 * mondes séparés : le corps du document ne pouvait pas citer ce que la personne
 * venait de saisir. C'est ce pont-là, et rien d'autre.
 *
 * ## Les trois garanties
 *
 * 1. **Une seule définition.** Le PDF signé et l'écran de signature appellent
 *    `interpolateBlockText` avec la même carte de valeurs. Ce qui est lu est ce
 *    qui est imprimé — la seule propriété qui compte sur un document qu'on signe.
 * 2. **Pas de récursion.** La substitution passe par une fonction de
 *    remplacement : une valeur saisie qui contiendrait elle-même `{{x}}`
 *    ressort littérale, jamais réinterprétée. Un champ de formulaire public est
 *    une entrée hostile, et ce module en fait du texte inerte.
 * 3. **Aucune référence morte au client.** Une clé inconnue est refusée **à la
 *    publication** (cf. `FormVersionSchemaContract`), et si malgré tout elle
 *    atteignait un rendu, elle s'affiche en case vide — jamais en `{{...}}`.
 *
 * Un bloc ne cite que des **champs**, jamais un autre bloc : un bloc n'a pas de
 * valeur, et s'autoriser à en citer un ouvrirait une récursion pour rien.
 */

/** Le motif, reconstruit à chaque appel : un littéral `/g` partagé porte un `lastIndex`. */
function motifRef(): RegExp {
  return /\{\{\s*([a-z][a-z0-9_]{0,62})\s*\}\}/g;
}

/** La forme lisible du motif, pour la microcopie de l'éditeur. */
export const BLOCK_REF_SYNTAX = "{{cle_du_champ}}";

/**
 * Ce qui s'affiche à la place d'une valeur absente.
 *
 * La convention du papier : une ligne à remplir. Personne ne la lit comme du
 * contenu, là où une chaîne vide donnerait « les services de , par l'entremise »
 * et où `{{cle}}` exposerait notre tuyauterie à un client.
 */
export const BLOCK_REF_ABSENT = "_____";

/** Les clés citées par un texte, dans l'ordre, sans doublon. */
export function collectBlockRefs(texte: string): string[] {
  const vues = new Set<string>();
  for (const m of texte.matchAll(motifRef())) {
    const cle = m[1];
    if (cle) vues.add(cle);
  }
  return [...vues];
}

/** Un bloc qui cite une clé que la version ne définit pas. */
export type BlockRefIssue = { block_key: string; unknown_refs: string[] };

/**
 * Collecte les références mortes d'une version. Pur, sans Zod — réutilisé par le
 * `superRefine` de publication ET par le pré-check de l'éditeur, exactement
 * comme `collectFieldRoleIssues`.
 *
 * Les deux langues sont inspectées : un gabarit dont seule la version anglaise
 * cite une clé morte est tout aussi cassé, et ne se verrait qu'en anglais.
 */
export function collectBlockRefIssues(
  sections: ReadonlyArray<{
    fields?: ReadonlyArray<{ key: string }>;
    blocks?: ReadonlyArray<{ key: string; text: { fr: string; en: string } }>;
  }>,
): BlockRefIssue[] {
  const champs = new Set(sections.flatMap((s) => (s.fields ?? []).map((f) => f.key)));
  const issues: BlockRefIssue[] = [];
  for (const section of sections) {
    for (const bloc of section.blocks ?? []) {
      const inconnues = [
        ...new Set([...collectBlockRefs(bloc.text.fr), ...collectBlockRefs(bloc.text.en)]),
      ].filter((cle) => !champs.has(cle));
      if (inconnues.length > 0) issues.push({ block_key: bloc.key, unknown_refs: inconnues });
    }
  }
  return issues;
}

/**
 * Rend une valeur de réponse **au fil d'une phrase**.
 *
 * Rend `null` quand il n'y a rien à dire — l'appelant met alors la case vide.
 * Ce n'est pas le même rendu que la ligne « libellé / valeur » d'un PDF, qui
 * affiche `-` pour dire « case laissée vide » : dans une ligne d'inventaire un
 * tiret est une information, au milieu d'une phrase c'est une faute. Un objet
 * ne se rend pas du tout : vider un JSON dans un paragraphe juridique serait
 * pire que la case vide.
 */
function valeurEnLigne(brut: unknown, langue: "fr" | "en"): string | null {
  if (brut === null || brut === undefined) return null;
  if (typeof brut === "boolean")
    return brut ? (langue === "fr" ? "Oui" : "Yes") : langue === "fr" ? "Non" : "No";
  if (Array.isArray(brut)) {
    const items = brut.map((v) => valeurEnLigne(v, langue)).filter((v): v is string => v !== null);
    return items.length > 0 ? items.join(", ") : null;
  }
  if (typeof brut === "object") return null;
  const t = String(brut).trim();
  return t.length > 0 ? t : null;
}

/**
 * Remplace les `{{cle}}` d'un texte de bloc par les valeurs fournies.
 *
 * @param texte   le texte du bloc dans la langue rendue
 * @param valeurs `form_responses.data` — ou, à l'écran, les valeurs en cours de
 *                saisie, préremplissage compris
 */
export function interpolateBlockText(
  texte: string,
  valeurs: Readonly<Record<string, unknown>>,
  langue: "fr" | "en",
): string {
  // Le remplaçant est une FONCTION : ni `$&` ni un `{{x}}` contenu dans une
  // valeur saisie ne peuvent être réinterprétés. C'est la garantie n° 2.
  return texte.replace(motifRef(), (_entier, cle: string) => {
    const rendu = Object.prototype.hasOwnProperty.call(valeurs, cle)
      ? valeurEnLigne(valeurs[cle], langue)
      : null;
    return rendu ?? BLOCK_REF_ABSENT;
  });
}

/**
 * Toutes les clés d'une version — champs ET blocs, un seul espace de noms.
 *
 * Une seule règle à expliquer, et aucune ambiguïté le jour où un bloc devrait
 * être adressable (une ancre, une traduction ciblée).
 */
function collectVersionKeys(
  sections: ReadonlyArray<{
    fields?: ReadonlyArray<{ key: string }>;
    blocks?: ReadonlyArray<{ key: string }>;
  }>,
): string[] {
  return sections.flatMap((sec) => [
    ...(sec.fields ?? []).map((f) => f.key),
    ...(sec.blocks ?? []).map((b) => b.key),
  ]);
}

/** Compare saved amounts without computing or inserting answers. */
const AmountOperandSchema = z
  .object({
    choice_field: StableKeySchema,
    custom_field: StableKeySchema,
    custom_choice: StableKeySchema,
    amounts: z
      .record(StableKeySchema, z.number().finite().positive())
      .refine((v) => Object.keys(v).length > 0 && Object.keys(v).length <= 100),
  })
  .strict();
const AmountRelationSchema = z
  .object({
    base: AmountOperandSchema,
    compared: AmountOperandSchema,
    multiplier: z.number().int().min(1).max(1000),
  })
  .strict();

export const FormSectionSchema = z
  .object({
    key: StableKeySchema,
    label: BilingualLabelSchema,
    // `min(0)` : une section peut n'avoir QUE du contenu — c'est le cas d'un
    // texte à lire avant de signer. La règle « au moins un champ ou un bloc »
    // est portée par le `superRefine` de la version, qui voit les deux.
    fields: z.array(FormFieldSchema).max(100),
    blocks: z.array(FormContentBlockSchema).max(50).optional(),
    coverage_choice_key: StableKeySchema.optional(),
    amount_relations: z.array(AmountRelationSchema).min(1).max(10).optional(),
  })
  .strict();

function validateAmountRelations(
  sections: z.infer<typeof FormSectionSchema>[],
  ctx: z.RefinementCtx,
) {
  sections.forEach((section, index) => {
    const fields = new Map(section.fields.map((field) => [field.key, field]));
    for (const relation of section.amount_relations ?? []) {
      for (const operand of [relation.base, relation.compared]) {
        const choice = fields.get(operand.choice_field);
        const custom = fields.get(operand.custom_field);
        const options = choice?.options?.map((option) => option.value) ?? [];
        if (
          choice?.type !== "select" ||
          custom?.type !== "number" ||
          !options.includes(operand.custom_choice) ||
          Object.hasOwn(operand.amounts, operand.custom_choice) ||
          options.some(
            (value) => value !== operand.custom_choice && !Object.hasOwn(operand.amounts, value),
          ) ||
          Object.keys(operand.amounts).some((value) => !options.includes(value)) ||
          !custom.active_when?.some(
            (condition) =>
              condition.field_key === operand.choice_field &&
              condition.values.length === 1 &&
              condition.values[0] === operand.custom_choice,
          )
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["sections", index, "amount_relations"],
            message:
              "amount relation requires complete select amounts and its conditional number field in the same section",
          });
        }
      }
      if (
        relation.base.choice_field === relation.compared.choice_field ||
        relation.base.custom_field === relation.compared.custom_field
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sections", index, "amount_relations"],
          message: "amount relation operands must be distinct",
        });
      }
    }
  });
}

/** Usage de la version ; absent sur l'historique, jamais déduit du nom. */
export const FormPurposeSchema = z.enum(["mandate", "business_spec", "other"]);
export function formPurpose(
  schema: unknown,
): "mandate" | "business_spec" | "other" | "unclassified" {
  const value = (schema as { version_metadata?: { purpose?: unknown } } | null)?.version_metadata
    ?.purpose;
  const parsed = FormPurposeSchema.safeParse(value);
  return parsed.success ? parsed.data : "unclassified";
}
export function hasSignatureFields(schema: unknown): boolean {
  const sections = (schema as { sections?: unknown } | null)?.sections;
  if (!Array.isArray(sections)) return false;
  return sections.some(
    (section) =>
      Array.isArray(section?.fields) &&
      section.fields.some((field: { role?: string }) => field?.role === "signature_consent"),
  );
}
export function isBusinessSpec(schema: unknown): boolean {
  return formPurpose(schema) === "business_spec" && !hasSignatureFields(schema);
}
const FormVersionMetadataSchema = z
  .object({
    schema_version: z.literal(1),
    purpose: FormPurposeSchema.optional(),
    document_profile: DocumentProfileSchema.optional(),
    content_languages: z
      .array(z.enum(["fr", "en"]))
      .min(1)
      .max(2)
      .refine((v) => new Set(v).size === v.length)
      .optional(),
  })
  .strict();

/** Contrat complet de `form_template_versions.schema` (v1). Ordre déterministe = ordre des tableaux. */
export const FormVersionSchemaContract = z
  .object({
    sections: z.array(FormSectionSchema).min(1).max(50),
    version_metadata: FormVersionMetadataSchema,
  })
  .strict()
  .superRefine((s, ctx) => {
    validateAmountRelations(s.sections, ctx);
    const sectionKeys = s.sections.map((x) => x.key);
    if (new Set(sectionKeys).size !== sectionKeys.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "clés de sections dupliquées",
        path: ["sections"],
      });
    }
    // Clés uniques dans TOUTE la version (agrégation cross-version). 9.BJ.2 —
    // champs ET blocs partagent l'espace de noms : un seul contrôle, une seule
    // règle à expliquer.
    const cles = collectVersionKeys(s.sections);
    if (new Set(cles).size !== cles.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "clés de champs ou de blocs dupliquées dans la version",
        path: ["sections"],
      });
    }

    if (s.version_metadata.document_profile && !isBusinessSpec(s)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["version_metadata", "document_profile"],
        message: "document profile requires an unsigned business specification",
      });
    }

    const fields = new Map(
      s.sections.flatMap((section) =>
        section.fields.map((field) => [field.key, { field, section }] as const),
      ),
    );
    const dependencies = new Map<string, string[]>();
    for (const { field, section } of fields.values()) {
      const deps = [...(field.active_when ?? []), ...(field.required_when ?? [])].map(
        (c) => c.field_key,
      );
      if (section.coverage_choice_key && field.key !== section.coverage_choice_key)
        deps.push(section.coverage_choice_key);
      dependencies.set(field.key, deps);
      if (!field.active_when && !field.required_when) continue;
      if (!isBusinessSpec(s) || field.role || field.prefill_from) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sections"],
          message:
            "conditional fields require an unsigned business specification without roles or prefill",
        });
      }
      for (const condition of [...(field.active_when ?? []), ...(field.required_when ?? [])]) {
        const source = fields.get(condition.field_key)?.field;
        if (
          !source ||
          source.type !== "select" ||
          !condition.values.every((v) => source.options?.some((o) => o.value === v))
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["sections"],
            message: `invalid condition for ${field.key}`,
          });
        }
      }
    }
    const visited = new Set<string>();
    const visiting = new Set<string>();
    const cyclic = (key: string): boolean => {
      if (visiting.has(key)) return true;
      if (visited.has(key)) return false;
      visiting.add(key);
      const result = (dependencies.get(key) ?? []).some(cyclic);
      visiting.delete(key);
      visited.add(key);
      return result;
    };
    if ([...dependencies.keys()].some(cyclic))
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sections"],
        message: "cyclic field conditions",
      });

    s.sections.forEach((section, index) => {
      if (!section.coverage_choice_key) return;
      const choice = section.fields.find((field) => field.key === section.coverage_choice_key);
      const values = choice?.options?.map((option) => option.value) ?? [];
      if (
        !isBusinessSpec(s) ||
        choice?.type !== "select" ||
        !choice.required ||
        choice.role ||
        choice.prefill_from ||
        values.length !== 3 ||
        !["included", "optional", "not_requested"].every((v) => values.includes(v))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sections", index, "coverage_choice_key"],
          message:
            "coverage choice requires a business specification and a required three-state selector in its own section",
        });
      }
    });

    // 9.BJ.2 — une section publiée doit dire quelque chose : au moins un champ
    // OU au moins un bloc. Avant ce chunk la règle était « ≥ 1 champ », ce qui
    // rendait une section de texte pur impossible à publier.
    s.sections.forEach((sec, i) => {
      if (sec.fields.length === 0 && (sec.blocks?.length ?? 0) === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "une section doit contenir au moins un champ ou un bloc",
          path: ["sections", i],
        });
      }
    });
    // 9.BJ.7 — un bloc ne part pas chez un client en citant un champ qui
    // n'existe pas. PUBLISH-only, comme les rôles : on écrit souvent le
    // paragraphe avant le champ qu'il cite, et un brouillon doit le permettre.
    for (const issue of collectBlockRefIssues(s.sections)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `bloc ${issue.block_key} : référence à un champ inexistant (${issue.unknown_refs
          .map((k) => `{{${k}}}`)
          .join(", ")})`,
        path: ["sections"],
      });
    }
    // 1e-B — compat type↔rôle + unicité d'un rôle par version (PUBLISH-only ;
    // le draft reste permissif). Même helper que le pré-check client.
    for (const issue of collectFieldRoleIssues(s.sections)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          issue.kind === "type_mismatch"
            ? `rôle ${issue.role} incompatible avec le type ${issue.field_type}`
            : `rôle ${issue.role} en double (${issue.field_keys.join(", ")})`,
        path: ["sections"],
      });
    }
  });

export type FormVersionSchemaType = z.infer<typeof FormVersionSchemaContract>;
export type FormFieldDef = z.infer<typeof FormFieldSchema>;

// ---------------------------------------------------------------------------
// Row schemas (projection wire ; création/patch = chunk 1b)
// ---------------------------------------------------------------------------
export const FormTemplateSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  code: z.string().min(1).max(200),
  name: z.string().min(1).max(300),
  description: z.string().nullable(),
  status: FormTemplateStatusSchema,
  scope_kind: FormTemplateScopeSchema,
  scope_ref_id: UuidSchema.nullable(),
  default_language: z.enum(["fr", "en"]),
  tags: z.array(z.string()),
  is_default: z.boolean(),
  created_by: UuidSchema.nullable(),
  updated_by: UuidSchema.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});

const FormVersionRevisionSchema = z.string().regex(/^[a-f0-9]{64}$/);

export const FormTemplateVersionSchema = z.object({
  revision: FormVersionRevisionSchema,
  id: UuidSchema,
  template_id: UuidSchema,
  tenant_id: UuidSchema,
  version_number: z.number().int().min(1),
  schema: FormVersionSchemaContract,
  changelog: z.string().nullable(),
  published_at: z.string().nullable(),
  published_by: UuidSchema.nullable(),
  source_file_id: UuidSchema.nullable(),
  generated_by_agent_id: UuidSchema.nullable(),
  generation_metadata: z.record(z.unknown()).nullable(),
  created_by: UuidSchema.nullable(),
  created_at: z.string(),
  deleted_at: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// Row schema LISTE dédié (F2-Forms-1c-iii). Contrat DISTINCT de FormTemplateSchema
// (décision PO D4) : la projection liste porte l'état de version calculé (LATERAL)
// pour distinguer sans ambiguïté aucune-version / brouillon-actif / dernière
// publiée / brouillon vN après une publiée. `latest_version_state` NULL = aucune
// version (données legacy). Aucun champ interne de génération exposé.
// ---------------------------------------------------------------------------
export const FormTemplateListItemSchema = FormTemplateSchema.extend({
  latest_version_id: UuidSchema.nullable(),
  latest_version_number: z.number().int().min(1).nullable(),
  latest_version_state: z.enum(["draft", "published"]).nullable(),
  active_draft_version_id: UuidSchema.nullable(),
  latest_published_at: z.string().nullable(),
  /**
   * 9.BB.1 — la dernière version PUBLIÉE, par son identifiant.
   *
   * La liste portait déjà sa date. Sans l'identifiant, proposer « remplir ce
   * modèle » obligeait à ouvrir la fiche du modèle pour y trouver la version à
   * remplir : le conseiller traversait l'administration des formulaires pour un
   * geste qui n'a rien d'administratif. NULL = aucune version publiée, donc rien
   * à remplir — c'est ce qui rend le modèle non proposable.
   */
  latest_published_version_id: UuidSchema.nullable(),
  latest_published_purpose: FormPurposeSchema.nullable().optional(),
  supports_client_signature: z.boolean().nullable().optional(),
});

export const FORM_RESPONSE_STATUSES = ["draft", "submitted", "locked"] as const;
export const FormResponseStatusSchema = z.enum(FORM_RESPONSE_STATUSES);

export const FormResponseSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  template_version_id: UuidSchema,
  project_id: UuidSchema.nullable(),
  intake_event_id: UuidSchema.nullable(),
  intake_link_id: UuidSchema.nullable(),
  data: z.record(z.unknown()),
  status: FormResponseStatusSchema,
  submitted_from: FormResponseSubmitSourceSchema.nullable(),
  submitted_by_user_id: UuidSchema.nullable(),
  submitted_by_agent_id: UuidSchema.nullable(),
  submitted_by_email: z.string().nullable(),
  submitted_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
  revision: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Contrat DRAFT permissif (édition en cours — 1b-i). Un brouillon peut être
// vide/partiel (0 section, section sans champ) MAIS reste structurellement sûr :
// schema_version=1, clés stables/uniques quand présentes, types connus, limites,
// aucune prop inconnue, options cohérentes, regex bornées, bilingue strict sur
// les éléments déjà créés. La publication utilise EXCLUSIVEMENT le contrat strict
// `FormVersionSchemaContract` (≥1 section, ≥1 champ). Sauvegardable ≠ publiable.
// ---------------------------------------------------------------------------
const FormSectionDraftSchema = z
  .object({
    key: StableKeySchema,
    label: BilingualLabelSchema,
    fields: z.array(FormFieldSchema).max(100), // min 0 : section temporairement vide
    blocks: z.array(FormContentBlockSchema).max(50).optional(),
    coverage_choice_key: StableKeySchema.optional(),
    amount_relations: z.array(AmountRelationSchema).min(1).max(10).optional(),
  })
  .strict();

export const FormVersionDraftSchemaContract = z
  .object({
    sections: z.array(FormSectionDraftSchema).max(50), // min 0 : brouillon vide
    version_metadata: FormVersionMetadataSchema,
  })
  .strict()
  .superRefine((s, ctx) => {
    validateAmountRelations(s.sections, ctx);
    const sectionKeys = s.sections.map((x) => x.key);
    if (new Set(sectionKeys).size !== sectionKeys.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "clés de sections dupliquées",
        path: ["sections"],
      });
    }
    // 9.BJ.2 — même espace de noms qu'à la publication : un brouillon qui
    // laisserait passer un doublon ne serait pas publiable, et la personne ne
    // l'apprendrait qu'au moment de publier.
    const cles = collectVersionKeys(s.sections);
    if (new Set(cles).size !== cles.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "clés de champs ou de blocs dupliquées dans la version",
        path: ["sections"],
      });
    }
  });

// ---------------------------------------------------------------------------
// Wire schemas — création / édition (1b-i API privée)
// ---------------------------------------------------------------------------
export const CreateFormTemplateSchema = z
  .object({
    code: z.string().trim().min(1).max(200),
    name: z.string().trim().min(1).max(300),
    description: z.string().max(2000).optional(),
    tags: z.array(z.string().max(50)).max(20).optional(),
    default_language: z.enum(["fr", "en"]).optional(),
  })
  .strict();

export const UpdateFormTemplateSchema = z
  .object({
    name: z.string().trim().min(1).max(300).optional(),
    description: z.string().max(2000).nullable().optional(),
    tags: z.array(z.string().max(50)).max(20).optional(),
  })
  .strict()
  .refine((o) => Object.keys(o).length > 0, { message: "au moins un champ à modifier" });

export const PatchFormVersionSchema = z
  .object({
    expected_revision: FormVersionRevisionSchema,
    schema: FormVersionDraftSchemaContract.optional(),
    changelog: z.string().max(2000).nullable().optional(),
  })
  .strict()
  .refine((o) => o.schema !== undefined || o.changelog !== undefined, {
    message: "au moins un champ à modifier",
  });

/**
 * Wire schema DÉDIÉ à l'intention `publish` (F2-Forms-1c-iii, décision PO D3) —
 * NE PAS réutiliser `PatchFormVersionSchema`. La publication est une seule
 * intention métier atomique : elle pose changelog + published_at + published_by
 * dans une unique transaction. `changelog` optionnel (MVP) ; body vide `{}` valide.
 */
export const PublishFormVersionSchema = z
  .object({
    changelog: z.string().max(2000).nullable().optional(),
  })
  .strict();

/** Filtres liste templates + pagination cursor/limit (convention repo). */
export const FormTemplateFilterSchema = PaginationSchema.extend({
  status: FormTemplateStatusSchema.optional(),
  scope_kind: FormTemplateScopeSchema.optional(),
  q: z.string().trim().max(200).optional(),
  tag: z.string().max(50).optional(),
});

// ---------------------------------------------------------------------------
// Génération depuis document (9.U.3) — wire + narrowing de la sortie LLM.
// ---------------------------------------------------------------------------
// Wire `.strict()` : le client ne fournit QUE le fichier source (résolu serveur),
// la clé d'idempotence et deux préférences. Tout le reste — tenant, storage path,
// `code`, `schema`, `status`, `version_number`, `generation_metadata` — est
// server-authoritative ; toute tentative de le poser est rejetée 400.
export const GenerateFormTemplateSchema = z
  .object({
    source_file_id: UuidSchema,
    /** Clé d'idempotence client, stable pour toute la session de dialogue. */
    generation_request_id: UuidSchema,
    name: z.string().trim().min(1).max(300).optional(),
    default_language: z.enum(["fr", "en"]).optional(),
  })
  .strict();

/**
 * Normalise les identifiants d'un schéma GÉNÉRÉ avant validation (9.U.3).
 *
 * Le modèle produit régulièrement des `key`/`options[].value` non conformes à
 * `^[a-z][a-z0-9_]{0,62}$` (accents, espaces, tirets, libellé recopié en valeur) —
 * mesuré en staging sur un formulaire réel. Le prompt le demande, mais **on ne
 * fait pas confiance à la sortie du LLM** : on normalise SERVEUR, de façon
 * déterministe, puis on valide. Aucune donnée n'est inventée : seuls les
 * identifiants techniques sont translittérés (les libellés restent intacts).
 *
 * Garanties : slug ASCII conforme · unicité (sections, champs globalement,
 * options par champ) par suffixe `_2`, `_3`… · repli indexé si le slug est vide.
 * Fonction PURE (aucun effet de bord) — testable en isolation.
 */
export function normalizeGeneratedSchemaKeys(input: unknown): unknown {
  const slug = (raw: unknown, fallback: string): string => {
    const s = String(raw ?? "")
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 63)
      .replace(/_+$/g, "");
    return /^[a-z]/.test(s) ? s : s.length > 0 ? `f_${s}`.slice(0, 63) : fallback;
  };
  const uniq = (candidate: string, taken: Set<string>): string => {
    if (!taken.has(candidate)) {
      taken.add(candidate);
      return candidate;
    }
    for (let i = 2; i < 1000; i++) {
      const next = `${candidate.slice(0, 60)}_${i}`;
      if (!taken.has(next)) {
        taken.add(next);
        return next;
      }
    }
    const fb = `k_${taken.size}`;
    taken.add(fb);
    return fb;
  };

  if (typeof input !== "object" || input === null) return input;
  const root = input as { sections?: unknown };
  if (!Array.isArray(root.sections)) return input;

  const sectionKeys = new Set<string>();
  const fieldKeys = new Set<string>();

  const sections = root.sections.map((rawSection, si) => {
    if (typeof rawSection !== "object" || rawSection === null) return rawSection;
    const section = { ...(rawSection as Record<string, unknown>) };
    section.key = uniq(slug(section.key, `section_${si + 1}`), sectionKeys);

    if (Array.isArray(section.fields)) {
      section.fields = section.fields.map((rawField, fi) => {
        if (typeof rawField !== "object" || rawField === null) return rawField;
        const field = { ...(rawField as Record<string, unknown>) };
        field.key = uniq(slug(field.key, `field_${si + 1}_${fi + 1}`), fieldKeys);

        if (Array.isArray(field.options)) {
          const optionValues = new Set<string>();
          field.options = field.options.map((rawOption, oi) => {
            if (typeof rawOption !== "object" || rawOption === null) return rawOption;
            const option = { ...(rawOption as Record<string, unknown>) };
            option.value = uniq(slug(option.value, `opt_${oi + 1}`), optionValues);
            return option;
          });
        }
        return field;
      });
    }
    return section;
  });

  return { ...(input as Record<string, unknown>), sections };
}

/**
 * Contrat de la sortie LLM : le contrat DRAFT permissif (types/clés/props/regex
 * déjà bornés, unicité des clés) **narrowed** par les bornes de génération.
 * La PUBLICATION reste validée par `FormVersionSchemaContract` strict — ce
 * narrowing ne peut donc jamais servir à contourner les règles de publication.
 */
export const GeneratedFormSchemaContract = FormVersionDraftSchemaContract.superRefine((s, ctx) => {
  if (s.sections.length > 20) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "trop de sections générées (max 20)",
      path: ["sections"],
    });
  }
  const totalFields = s.sections.reduce((n, sec) => n + sec.fields.length, 0);
  if (totalFields > 120) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "trop de champs générés (max 120)",
      path: ["sections"],
    });
  }
  s.sections.forEach((sec, si) => {
    sec.fields.forEach((f, fi) => {
      if (f.options && f.options.length > 50) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "trop d'options générées (max 50)",
          path: ["sections", si, "fields", fi, "options"],
        });
      }
    });
  });
});

// ---------------------------------------------------------------------------
// Registre des réponses (9.U.7) — liste + export CSV broker (human-only).
// ---------------------------------------------------------------------------
// Filtres partagés IDENTIQUES entre `GET /v1/form-responses` et `GET /csv`
// (un seul builder côté route → aucune divergence). Bornes reprises des patterns
// existants (PaginationSchema : limit 1-200 défaut 50 ; dates ISO). `from`/`to`
// bornent `created_at` (incluses) ; `from <= to` imposé. Le filtre `submitted_from`
// ne matche que les valeurs non-NULL (les brouillons legacy à source NULL ne sont
// pas ciblables — comportement v1 assumé).
export const FormResponseFilterSchema = PaginationSchema.extend({
  status: FormResponseStatusSchema.optional(),
  submitted_from: FormResponseSubmitSourceSchema.optional(),
  template_id: UuidSchema.optional(),
  template_version_id: UuidSchema.optional(),
  project_id: UuidSchema.optional(),
  intake_link_id: UuidSchema.optional(),
  /** ISO datetime — borne basse `created_at >= from` (incluse). */
  from: z.string().datetime().optional(),
  /** ISO datetime — borne haute `created_at <= to` (incluse). */
  to: z.string().datetime().optional(),
}).refine((v) => !(v.from && v.to) || v.from <= v.to, {
  message: "`from` doit être antérieur ou égal à `to`.",
  path: ["from"],
});

// Projection POSITIVE explicite exposée au wire (jamais une ligne DB étalée).
// EXCLUT strictement : `data` (réponses = PII), `client_ip_hash` (PII forensic),
// `tenant_id` (implicite), `submitted_by_email` + `submitted_by_user_id` +
// `submitted_by_agent_id` (identité soumetteur — non exposée). Colonnes ci-dessous
// = auditables / non-PII. `processing_status` = statut de l'`intake_event` lié
// (LEFT JOIN) quand il existe, sinon NULL (« Non traité » côté UI) — jamais un
// nouveau statut persistant.
export const FormResponseListItemSchema = z.object({
  purpose: FormPurposeSchema.nullable().optional(),
  id: UuidSchema,
  status: FormResponseStatusSchema,
  submitted_from: FormResponseSubmitSourceSchema.nullable(),
  submitted_at: z.string().nullable(),
  created_at: z.string(),
  template_version_id: UuidSchema,
  template_id: UuidSchema,
  template_name: z.string(),
  version_number: z.number().int().min(1),
  project_id: UuidSchema.nullable(),
  project_title: z.string().nullable(),
  project_reference: z.string().nullable(),
  intake_link_id: UuidSchema.nullable(),
  intake_event_id: UuidSchema.nullable(),
  processing_status: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// Wire schemas — réponses (F2-Forms-1b-ii / 9.U.2, API privée broker)
// ---------------------------------------------------------------------------
// `data` est validé au niveau route contre la version EXACTE liée
// (`buildFormResponseDataSchema(version.schema, {mode})`) — jamais ici : le wire
// garantit seulement un objet JSON. Ce flow privé n'accepte QUE la version cible
// et les données ; tous les liens de contexte (`intake_event_id`, `intake_link_id`,
// `project_id`) sont **server-authoritative** (posés par le flux qui crée la
// liaison — intake/public/matérialisation — jamais par le broker), et toute
// colonne serveur (`status`, `submitted_*`) est interdite : `.strict()` → 400.
export const CreateFormResponseSchema = z
  .object({
    template_version_id: UuidSchema,
    data: z.record(z.unknown()).optional(),
    /**
     * 9.W.2 — clé d'idempotence OPTIONNELLE générée par le client, stable le
     * temps de la session de remplissage (`D-NEW-FORMS-1DII-CREATE-DUP-WINDOW`).
     *
     * Sans elle, un `POST` commité côté serveur dont la réponse HTTP est perdue
     * laisse l'utilisateur créer un second brouillon — porteur de PII et soumis
     * à une rétention longue. Avec elle, le rejeu renvoie le brouillon existant.
     *
     * `UuidSchema` et non une chaîne libre : une clé opaque de longueur fixe ne
     * peut pas servir de canal pour faire entrer des données du client dans une
     * colonne indexée. Optionnelle pour ne pas casser les appelants existants.
     */
    client_draft_id: UuidSchema.optional(),
    /**
     * 9.BB.1 — le dossier auquel cette réponse appartient.
     *
     * ## Pourquoi ce champ n'existait pas
     *
     * La règle d'origine était : les liens de contexte (`project_id`,
     * `intake_event_id`, `intake_link_id`) sont posés par le serveur, jamais
     * proposés par l'appelant. Elle était juste tant que le SEUL producteur du
     * lien était le lien public client — l'`intake_link` porte son projet, et
     * personne n'a à le dire.
     *
     * Elle fermait en revanche, **par construction**, le parcours du conseiller :
     * `POST /v1/quote-requests/:id/spec` exige que la réponse porte le même
     * dossier que la demande, et rien côté conseiller ne pouvait poser ce lien.
     * Mesuré en base : 8 réponses sur 8 rattachées à un dossier venaient d'un
     * lien public, zéro d'un humain du cabinet.
     *
     * ## Ce qui ne change pas
     *
     * L'autorité reste au serveur. Le client **propose** un identifiant ; le
     * serveur vérifie que ce dossier existe dans SON cabinet et refuse d'un seul
     * code sinon. Le lien est ensuite **immuable** (trigger 0080), et l'attach
     * du cahier de charges revérifie l'égalité des dossiers — c'est lui qui porte
     * l'autorisation, pas cette création.
     *
     * `intake_event_id` et `intake_link_id` restent, eux, strictement serveur.
     */
    project_id: UuidSchema.optional(),
  })
  .strict();

export const PatchFormResponseSchema = z
  .object({
    expected_revision: z.string().min(1).max(100).optional(),
    data: z.record(z.unknown()),
  })
  .strict();

/**
 * Wire schema de l'intention métier atomique `submit-to-intake` (F2-Forms-1d-i).
 * SEUL `project_id` est accepté — destination du workflow, validée serveur (même
 * tenant, non supprimé). `.strict()` rejette tout autre champ (400) : jamais
 * `intake_event_id`/`status`/`submitted_*`/`data`/`template_version_id` au wire.
 * `project_id` n'est PAS ajouté aux schemas create/PATCH génériques de réponse.
 */
export const SubmitFormResponseToIntakeSchema = z
  .object({
    expected_revision: z.string().min(1).max(100).optional(),
    project_id: UuidSchema,
  })
  .strict();

// ---------------------------------------------------------------------------
// Helper partagé — validateur de réponse construit À PARTIR d'une version précise
// ---------------------------------------------------------------------------
/** Exact comparison of the finite JSON numbers' decimal representations. */
function matchesAmountRatio(base: number, compared: number, multiplier: number): boolean {
  const decimal = (value: number) => {
    const [coefficient, exponent = "0"] = value.toString().split("e");
    const [whole, fraction = ""] = coefficient!.split(".");
    return { units: BigInt(`${whole}${fraction}`), power: Number(exponent) - fraction.length };
  };
  const a = decimal(base);
  const b = decimal(compared);
  const power = Math.min(a.power, b.power);
  return (
    a.units * BigInt(multiplier) * 10n ** BigInt(a.power - power) ===
    b.units * 10n ** BigInt(b.power - power)
  );
}

/**
 * Construit un schéma Zod STRICT pour `form_response.data` à partir du contrat
 * `schema` d'une version publiée. Rejette toute clé inconnue (Q5), vérifie
 * types/options. Le validateur est lié à CETTE version — jamais à la dernière
 * (ADR Q2). Consommé par l'API réponses (1b-ii) et le FormRenderer (miroir client).
 *
 * `mode` (source de vérité unique, dérivée du MÊME contrat de champ) :
 *   - `"draft"`  : édition en cours — champs requis rendus OPTIONNELS ; une clé
 *                  présente doit exister dans la version et être bien typée ;
 *                  options respectées ; aucune clé inconnue ; aucune coercition.
 *   - `"submit"` : soumission — champs requis OBLIGATOIRES ; types exacts ;
 *                  options respectées ; aucune clé inconnue.
 * Sauvegardable (`draft`) ≠ soumissible (`submit`) : miroir de la doctrine
 * brouillon/publication du versionnage (1b-i).
 */
export function buildFormResponseDataSchema(
  formSchema: FormVersionSchemaType,
  options: { mode: "draft" | "submit" },
): z.ZodType<Record<string, unknown>> {
  const draft = options.mode === "draft";
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const section of formSchema.sections) {
    for (const f of section.fields) {
      let base: z.ZodTypeAny;
      switch (f.type) {
        case "text":
        case "textarea": {
          let s = z.string();
          if (f.validation?.min_length !== undefined) s = s.min(f.validation.min_length);
          if (f.validation?.max_length !== undefined) s = s.max(f.validation.max_length);
          if (f.validation?.regex !== undefined) s = s.regex(new RegExp(f.validation.regex));
          base = s;
          break;
        }
        case "email":
          base = z.string().email();
          break;
        case "number": {
          let numeric = z.number().finite();
          if (f.validation?.min !== undefined) numeric = numeric.min(f.validation.min);
          if (f.validation?.exclusive_min !== undefined)
            numeric = numeric.gt(f.validation.exclusive_min);
          if (f.validation?.max !== undefined) numeric = numeric.max(f.validation.max);
          if (f.validation?.integer) numeric = numeric.int();
          base = numeric;
          break;
        }
        case "date":
          base = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date ISO YYYY-MM-DD");
          break;
        case "checkbox":
          base = z.boolean();
          break;
        case "select":
          base = z.enum((f.options ?? []).map((o) => o.value) as [string, ...string[]]);
          break;
        case "multi_select":
          base = z.array(z.enum((f.options ?? []).map((o) => o.value) as [string, ...string[]]));
          break;
        case "file":
          base = z.array(UuidSchema);
          break;
        case "company_ref":
        case "contact_ref":
          base = UuidSchema;
          break;
        default:
          base = z.unknown();
      }
      // Requis honoré uniquement au submit ; le brouillon rend tout optionnel.
      shape[f.key] =
        !draft &&
        f.required &&
        !f.active_when &&
        !f.required_when &&
        (!section.coverage_choice_key || f.key === section.coverage_choice_key)
          ? base
          : base.optional();
    }
  }
  // .strict() → rejette toute clé de réponse absente du schéma (Q5), dans les 2 modes.
  return z
    .object(shape)
    .strict()
    .superRefine((data, ctx) => {
      if (draft) return;
      const active = activeFormFieldKeys(formSchema.sections, data);
      for (const section of formSchema.sections) {
        if (!isCoverageSectionActive(section, data)) continue;
        for (const relation of section.amount_relations ?? []) {
          const resolve = (operand: z.infer<typeof AmountOperandSchema>) => {
            if (!active.has(operand.choice_field)) return undefined;
            const choice = data[operand.choice_field];
            if (choice === operand.custom_choice && !active.has(operand.custom_field))
              return undefined;
            return choice === operand.custom_choice
              ? data[operand.custom_field]
              : typeof choice === "string" && Object.hasOwn(operand.amounts, choice)
                ? operand.amounts[choice]
                : undefined;
          };
          const base = resolve(relation.base);
          const compared = resolve(relation.compared);
          // Omitted optional amounts remain omitted. Required guards still apply below.
          if (typeof base !== "number" || typeof compared !== "number") continue;
          // Zod can continue refinements after finite() marks a numeric field invalid.
          if (!Number.isFinite(base) || !Number.isFinite(compared)) continue;
          const expected = base * relation.multiplier;
          if (
            !Number.isFinite(expected) ||
            !matchesAmountRatio(base, compared, relation.multiplier)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [
                data[relation.compared.choice_field] === relation.compared.custom_choice
                  ? relation.compared.custom_field
                  : relation.compared.choice_field,
              ],
              message: "Amounts must match the configured ratio",
            });
          }
        }
      }
      const required = requiredFormFieldKeys(formSchema.sections, data);
      for (const section of formSchema.sections) {
        for (const field of section.fields) {
          if (
            required.has(field.key) &&
            (data[field.key] === undefined ||
              ((field.active_when || field.required_when) &&
                typeof data[field.key] === "string" &&
                !(data[field.key] as string).trim()))
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.invalid_type,
              expected: "unknown",
              received: "undefined",
              path: [field.key],
              message: "Required",
            });
          }
        }
      }
    });
}

/** Shared interpretation for HTTP validation, browser and PDF. No default choice. */
export function isCoverageSectionActive(
  section: { coverage_choice_key?: string },
  data: Record<string, unknown>,
): boolean {
  if (!section.coverage_choice_key) return true;
  return (
    data[section.coverage_choice_key] === "included" ||
    data[section.coverage_choice_key] === "optional"
  );
}

type ConditionalSection = {
  coverage_choice_key?: string;
  fields: readonly {
    key: string;
    required?: boolean;
    active_when?: FormFieldConditions;
    required_when?: FormFieldConditions;
  }[];
};

/** Evaluate dependencies against active sources, never stale hidden answers. */
export function activeFormFieldKeys(
  sections: readonly ConditionalSection[],
  data: Record<string, unknown>,
): Set<string> {
  const fields = new Map(
    sections.flatMap((section) =>
      section.fields.map((field) => [field.key, { field, section }] as const),
    ),
  );
  const memo = new Map<string, boolean>();
  const visiting = new Set<string>();
  const active = (key: string): boolean => {
    if (memo.has(key)) return memo.get(key)!;
    const entry = fields.get(key);
    if (!entry || visiting.has(key)) return false;
    visiting.add(key);
    const { field, section } = entry;
    const choice = section.coverage_choice_key;
    const result =
      (!choice || key === choice || (active(choice) && isCoverageSectionActive(section, data))) &&
      (field.active_when ?? []).every(
        (c) =>
          active(c.field_key) &&
          typeof data[c.field_key] === "string" &&
          c.values.includes(data[c.field_key] as string),
      );
    visiting.delete(key);
    memo.set(key, result);
    return result;
  };
  return new Set([...fields.keys()].filter(active));
}

/** Requiredness is configured by this version, never by tenant names or field keys. */
export function requiredFormFieldKeys(
  sections: readonly ConditionalSection[],
  data: Record<string, unknown>,
): Set<string> {
  const active = activeFormFieldKeys(sections, data);
  return new Set(
    sections
      .flatMap((s) => s.fields)
      .filter(
        (f) =>
          f.required &&
          active.has(f.key) &&
          (f.required_when ?? []).every(
            (c) =>
              active.has(c.field_key) &&
              typeof data[c.field_key] === "string" &&
              c.values.includes(data[c.field_key] as string),
          ),
      )
      .map((f) => f.key),
  );
}

/** Display projection only: never overwrite the broker's saved values. */
export function projectCoverageDisplayData(
  sections: readonly ConditionalSection[],
  data: Record<string, unknown>,
): Record<string, unknown> {
  const displayed = { ...data };
  const active = activeFormFieldKeys(sections, data);
  for (const section of sections)
    for (const field of section.fields) {
      if (!active.has(field.key)) delete displayed[field.key];
    }
  return displayed;
}

/** Version-scoped content language, independent of the application's UI language. */
export function formContentLanguage(
  schema: unknown,
  requested: "fr" | "en",
  legacyDefault?: "fr" | "en",
): "fr" | "en" {
  const raw = (schema as { version_metadata?: { content_languages?: unknown } } | null)
    ?.version_metadata?.content_languages;
  if (!Array.isArray(raw) || raw.length === 0) return legacyDefault ?? requested;
  return raw.includes(requested) ? requested : raw[0] === "en" ? "en" : "fr";
}

// -----------------------------------------------------------------------------
// Champs de référence non remplissables en contexte public (chunk 9.AC.1)
// -----------------------------------------------------------------------------

/**
 * Types de champs qu'un client public ne peut PAS remplir, et qui n'ont pas de
 * valeur par défaut.
 *
 * `company_ref` et `contact_ref` désignent une entité du cabinet. Les rendre
 * remplissables côté public exigerait de lister les entreprises ou les contacts du
 * tenant à un visiteur anonyme — c'est-à-dire d'offrir le portefeuille client du
 * cabinet à qui possède un lien. Ils restent donc en lecture seule sur le parcours
 * public, définitivement, et ce n'est pas une lacune d'implémentation.
 */
export const PUBLIC_UNFILLABLE_FIELD_TYPES = ["company_ref", "contact_ref"] as const;

/**
 * Les champs REQUIS d'un schéma qu'un client public ne pourra jamais remplir.
 *
 * Un tel champ crée un **cul-de-sac** : la validation `mode: "submit"` l'exige, le
 * renderer public ne peut pas le saisir, donc le client ne peut jamais soumettre —
 * et le dossier ne peut jamais devenir prêt. Le cas existait avant ce chunk, sans
 * que rien ne le signale : on créait le lien, on l'envoyait, et l'échec se
 * découvrait chez le client.
 *
 * Renvoie les clés fautives, pour que le message dise LESQUELLES.
 */
export function publicUnfillableRequiredFields(schema: unknown): string[] {
  const s = schema as { sections?: { fields?: FormFieldDef[] }[] } | null | undefined;
  if (!s?.sections) return [];
  const interdits = new Set<string>(PUBLIC_UNFILLABLE_FIELD_TYPES);
  const fautifs: string[] = [];
  for (const section of s.sections) {
    for (const f of section.fields ?? []) {
      if (f.required && interdits.has(f.type)) fautifs.push(f.key);
    }
  }
  return fautifs;
}
